'use client';

/**
 * The kids module shell: theme scope + state provider + navigation.
 *
 * `.kids-theme` is what swaps the entire design system (see styles/kids.css).
 * Because it re-declares the same CSS custom properties the rest of the app
 * uses, every Tailwind utility inside this subtree renders in the playful
 * palette without a single component knowing which theme it is in.
 *
 * The shell deliberately does NOT gate on having a profile. It used to, and
 * that was a serious mistake: ProfileGate renders a skeleton until the client
 * hydrates, so wrapping the whole page in it meant every word of crawlable
 * copy stayed out of the DOM and `/kids` served roughly 350 characters to a
 * crawler. Gating is now the caller's job, applied to the interactive widget
 * alone, so the prose around it always renders on the server.
 */
import React from 'react';
import Link from 'next/link';
import { KidsProvider } from '../context/KidsProvider';
import KidsNav from './KidsNav';

export default function KidsShell({
  children,
  /** Lesson pages hide the chrome so nothing competes with the exercise. */
  bare = false,
}: {
  children: React.ReactNode;
  bare?: boolean;
}) {
  return (
    <KidsProvider>
      <div className="kids-theme min-h-screen flex flex-col">
        <a
          href="#kids-main"
          className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:m-3 focus:px-4 focus:py-2 focus:rounded-xl focus:bg-[var(--accent)] focus:text-white focus:font-bold"
        >
          Skip to content
        </a>

        {!bare && <KidsNav />}

        <main id="kids-main" className="flex-1 w-full pb-24 md:pb-10">
          {children}
        </main>

        {!bare && (
          <footer className="k-no-print py-8 mt-8" style={{ borderTop: '2px solid var(--border)' }}>
            <div className="max-w-screen-xl mx-auto px-4 sm:px-6 flex flex-wrap items-center justify-between gap-4 text-sm">
              <p className="text-muted-foreground">
                Kids Typing Journey — free, no account, progress saved on this device.
              </p>
              <nav aria-label="Kids footer" className="flex flex-wrap gap-x-5 gap-y-2 font-bold">
                <Link href="/kids/faq" className="hover:text-[var(--accent)]">
                  Questions
                </Link>
                <Link href="/kids/teachers" className="hover:text-[var(--accent)]">
                  For teachers
                </Link>
                <Link href="/kids/parents" className="hover:text-[var(--accent)]">
                  For parents
                </Link>
                <Link href="/privacy" className="hover:text-[var(--accent)]">
                  Privacy
                </Link>
                <Link href="/" className="hover:text-[var(--accent)]">
                  TypeSwift home
                </Link>
              </nav>
            </div>
          </footer>
        )}
      </div>
    </KidsProvider>
  );
}
